import { useState } from 'react';

export const useCrudOperations = (baseURL, setData, data) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const token = localStorage.getItem('token');  // Hämta token från localStorage

  // Lägg till
  const addItem = async (newItem, isFormData = false) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(baseURL, {
        method: 'POST',
        headers: isFormData
          ? { 'Authorization': `Bearer ${token}` }
          : {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
        body: isFormData ? newItem : JSON.stringify(newItem),
      });
      
      if (!response.ok) {
        // Försök tolka felmeddelandet från servern om möjligt
        try {
          const errorData = await response.json();
          console.error('Server error details:', errorData);
          throw new Error(errorData.message || `Server error: ${response.status} ${response.statusText}`);
        } catch (jsonError) {
          // Om vi inte kan tolka JSON, använd status text
          throw new Error(`${response.status} ${response.statusText}`);
        }
      }
      
      const addedItem = await response.json();

      if (setData && data) {
        setData([...data, addedItem]);
      }
      return addedItem;
    } catch (error) {
      console.error('Add item error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Lägg till med formData för filuppladdning
  const addItemWithFile = async (formData) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Logga vad som skickas, utan att visa filinnehåll
      const formDataEntries = {};
      for (let [key, value] of formData.entries()) {
        if (value instanceof File) {
          formDataEntries[key] = `File: ${value.name} (${value.size} bytes)`;
        } else {
          formDataEntries[key] = value;
        }
      }
      const response = await fetch(baseURL, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          // OBS: Ta INTE med 'Content-Type' för FormData, browsern sätter det automatiskt
        },
        body: formData,
      });
      
      if (!response.ok) {
        try {
          const errorData = await response.json();
          console.error('Server error details:', errorData);
          throw new Error(errorData.message || `Server error: ${response.status} ${response.statusText}`);
        } catch (jsonError) {
          console.error('Could not parse error response as JSON:', jsonError);
          const errorText = await response.text();
          throw new Error(`${response.status} ${response.statusText}: ${errorText.substring(0, 200)}`);
        }
      }
      
      const addedItem = await response.json();

      if (setData && data) {
        setData([...data, addedItem]);
      }
      return addedItem;
    } catch (error) {
      console.error('Add item with file error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Ta bort
  const removeItem = async (id) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${baseURL}/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,  // Lägg till token i Authorization-headern
        },
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Error deleting item: ${errorText}`);
      }

      if (setData && data && Array.isArray(data)) {
        setData(data.filter(item => item._id !== id));
      }
    } catch (error) {
      setError(error.message);
      console.error('Delete error:', error);
      throw error; // Kasta om felet för att hantera det i komponenten
    } finally {
      setIsLoading(false);
    }
  };

  // Uppdatera
  const updateItem = async (id, updatedData, isFormData = false) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${baseURL}/${id}`, {
        method: 'PUT',
        headers: isFormData
          ? { 'Authorization': `Bearer ${token}` }
          : {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
        body: isFormData ? updatedData : JSON.stringify(updatedData),
      });
      
      if (!response.ok) {
        let errorMessage = 'Error updating item';
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch {
          const errorText = await response.text();
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }
      
      const updatedItem = await response.json();
      if (setData && data) {
        setData(prevData => 
          prevData.map(item => item._id === id ? updatedItem : item)
        );
      }
      return updatedItem;
    } catch (error) {
      setError(error.message);
      console.error('Update error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return { addItem, addItemWithFile, removeItem, updateItem, isLoading, error };
};